import { useCallback } from 'react';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPaymentMethod } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';

/**
 * Hook to validate, against loyalty rules, the given cart entries
 */
export const useLoyaltyCartEntriesEvaluation = () => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const enableRewardCooldown = useFlag(LaunchDarklyFlag.ENABLE_REWARD_COOLDOWN);

  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { evaluateLoyaltyUserOffers, evaluateLoyaltyUserRewards } = useLoyaltyContext();

  const evaluateLoyaltyCartEntries = useCallback(
    async (
      loyaltyId: string,
      cartEntries?: ILegacyCartEntry[],
      subtotalAmount?: number,
      paymentMethod?: LoyaltyPaymentMethod | null
    ) => {
      const promises: Promise<void>[] = [];

      if (appliedLoyaltyOffers.length) {
        promises.push(
          evaluateLoyaltyUserOffers(
            loyaltyId,
            appliedLoyaltyOffers,
            cartEntries,
            appliedLoyaltyRewards,
            subtotalAmount,
            paymentMethod
          )
        );
      }

      if (
        (enableRewardUnstacking || enableRewardCooldown) &&
        Object.values(appliedLoyaltyRewards).length
      ) {
        promises.push(
          evaluateLoyaltyUserRewards(
            loyaltyId,
            appliedLoyaltyRewards,
            cartEntries,
            appliedLoyaltyOffers,
            subtotalAmount,
            paymentMethod
          )
        );

        await Promise.all(promises);
      }
    },
    [
      appliedLoyaltyOffers,
      appliedLoyaltyRewards,
      evaluateLoyaltyUserOffers,
      evaluateLoyaltyUserRewards,
      enableRewardUnstacking,
      enableRewardCooldown,
    ]
  );

  return evaluateLoyaltyCartEntries;
};
